import { useEffect, useRef, useState } from 'react'

import { DangerButton, PrimaryButton, Space, Text } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { ReportAccessUserSelect } from '@webapp/platform/iam/components/ReportAccessUserSelect'
import { type User } from '@webapp/platform/iam/entities/User'
import messages from './messages'
import { type ReportAccessSubmitActionEnum } from '../../enums'
import { ReportAccessSubmitActionSelect } from '../ReportAccessSubmitActionSelect'

const USER_SELECT_INPUT_ID = 'report-access-user-select'

export interface IReportAccessActionRowProps {
  /** ALL-PrimaryCategories mode renders the ADD/REMOVE action select + Submit */
  isAllPrimaryCategories: boolean
  /** Value of the ADD/REMOVE action select */
  submitAction: ReportAccessSubmitActionEnum
  /** Submit revokes (ALL › Remove): danger styling and the revoke helper */
  isRemoveAction: boolean
  onSubmitActionChange: (action: ReportAccessSubmitActionEnum) => void
  onSubmit: (user: User) => void
  isSubmitting: boolean
  isSubmitDisabled: boolean
  /**
   * Bumped by the Feature Set when a command or confirmation the picker
   * started has settled; focus returns to the picker once it is enabled
   */
  userPickerFocusRequest: number
}

/**
 * Presentational Feature Set subcomponent — the user picker action row.
 * Holds only the transient picker selection; every action fires a Feature
 * Set callback.
 */
export const ReportAccessActionRow = ({
  isAllPrimaryCategories,
  submitAction,
  isRemoveAction,
  onSubmitActionChange,
  onSubmit,
  isSubmitting,
  isSubmitDisabled,
  userPickerFocusRequest,
}: IReportAccessActionRowProps) => {
  const intl = useProcurifyIntl()
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const handledFocusRequestRef = useRef(userPickerFocusRequest)

  // The submit button and the dialogs' triggers are disabled once a command
  // runs, so focus would otherwise fall to the page body. Focus applied while
  // the picker is still disabled is lost, hence the gate.
  useEffect(() => {
    if (isSubmitDisabled) return
    if (userPickerFocusRequest === handledFocusRequestRef.current) return

    handledFocusRequestRef.current = userPickerFocusRequest
    document.getElementById(USER_SELECT_INPUT_ID)?.focus()
  }, [userPickerFocusRequest, isSubmitDisabled])

  const handleSubmit = () => {
    if (!selectedUser) return
    onSubmit(selectedUser)
    setSelectedUser(null)
  }

  // Danger styling in REMOVE mode (BHV-024)
  const SubmitButtonComponent = isRemoveAction ? DangerButton : PrimaryButton

  return (
    <Space size='small'>
      <Space direction='horizontal' align='center'>
        {isAllPrimaryCategories && (
          <Space.Item minWidth={180}>
            <ReportAccessSubmitActionSelect
              value={submitAction}
              onChange={onSubmitActionChange}
            />
          </Space.Item>
        )}
        <Space.Item grow={1}>
          <ReportAccessUserSelect
            inputId={USER_SELECT_INPUT_ID}
            value={selectedUser}
            onChange={setSelectedUser}
            isDisabled={isSubmitDisabled}
          />
        </Space.Item>
        <SubmitButtonComponent
          type='button'
          disabled={!selectedUser || isSubmitDisabled}
          isLoading={isSubmitting}
          onClick={handleSubmit}
        >
          {intl.formatMessage(
            isAllPrimaryCategories
              ? messages.submitButtonLabel
              : messages.addButtonLabel
          )}
        </SubmitButtonComponent>
      </Space>

      {isRemoveAction && (
        <Text color='textSecondary'>
          {intl.formatMessage(messages.removeHelperText)}
        </Text>
      )}
    </Space>
  )
}

ReportAccessActionRow.displayName = 'ReportAccessActionRow'
